import { useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

interface PromptTypeData {
  prompt_type: string;
  count: number;
}

interface PromptTypesPieChartProps {
  data: PromptTypeData[];
  height?: number;
}

const COLORS = [
  "hsl(25, 95%, 60%)",
  "hsl(190, 80%, 55%)",
  "hsl(280, 60%, 55%)",
  "hsl(45, 95%, 55%)",
  "hsl(150, 60%, 45%)",
  "hsl(0, 84%, 60%)",
];

interface LegendItemProps {
  color: string;
  label: string;
  count: number;
  percentage: number;
}

function LegendItem({ color, label, count, percentage }: LegendItemProps) {
  return (
    <div className="flex items-center gap-2 min-w-0">
      <span
        className="h-3 w-3 rounded-full flex-shrink-0"
        style={{ backgroundColor: color }}
      />
      <span
        className="text-sm text-foreground min-w-0 break-words"
        style={{ overflowWrap: "anywhere" }}
      >
        {label}
      </span>
      <span className="text-sm text-muted-foreground whitespace-nowrap ml-auto">
        {count} ({percentage}%)
      </span>
    </div>
  );
}

export function PromptTypesPieChart({ data, height = 200 }: PromptTypesPieChartProps) {
  const [showAll, setShowAll] = useState(false);
  const isMobile = useIsMobile();

  if (data.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-muted-foreground"
        style={{ height }}
      >
        No submissions yet
      </div>
    );
  }

  // Calculate total and sort by count (largest first)
  const total = data.reduce((sum, item) => sum + item.count, 0);
  const sortedData = [...data].sort((a, b) => b.count - a.count);

  // Calculate percentages
  const dataWithPercentage = sortedData.map((item, index) => ({
    ...item,
    percentage: Math.round((item.count / total) * 100),
    color: COLORS[index % COLORS.length],
  }));

  // Determine how many to show
  const hasMany = dataWithPercentage.length > 6;
  const displayedData = hasMany && !showAll
    ? dataWithPercentage.slice(0, 6)
    : dataWithPercentage;

  // Calculate chart height (reduce if we need space for legend)
  const chartHeight = Math.min(height - 20, 160);

  return (
    <div className="w-full min-w-0">
      {/* Pie Chart - no external labels */}
      <ResponsiveContainer width="100%" height={chartHeight}>
        <PieChart>
          <Pie
            data={sortedData}
            dataKey="count"
            nameKey="prompt_type"
            cx="50%"
            cy="50%"
            outerRadius={chartHeight / 2.5}
            label={false}
            labelLine={false}
          >
            {sortedData.map((_, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload || !payload.length) return null;
              const item = payload[0];
              const value = item.value as number;
              const name = item.name as string;
              const pct = Math.round((value / total) * 100);
              return (
                <div className="bg-popover border border-border rounded-md px-3 py-2 shadow-md">
                  <span className="text-popover-foreground text-sm">
                    {name} — {value} ({pct}%)
                  </span>
                </div>
              );
            }}
          />
        </PieChart>
      </ResponsiveContainer>

      {/* Legend below chart */}
      <div className="mt-3 w-full min-w-0">
        <div className="grid grid-cols-1 gap-2">
          {displayedData.map((item) => (
            <LegendItem
              key={item.prompt_type}
              color={item.color}
              label={item.prompt_type}
              count={item.count}
              percentage={item.percentage}
            />
          ))}
        </div>

        {/* Show all toggle */}
        {hasMany && (
          <Button
            variant="ghost"
            size="sm"
            className="w-full mt-2 text-muted-foreground hover:text-foreground"
            onClick={() => setShowAll(!showAll)}
          >
            {showAll ? (
              <>
                <ChevronUp className="h-4 w-4 mr-1" />
                Show less
              </>
            ) : (
              <>
                <ChevronDown className="h-4 w-4 mr-1" />
                Show all ({dataWithPercentage.length})
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
}
